import { inject, Injectable, Injector } from '@angular/core';
import {
  getShapeForEventTracking,
  ShapeState,
  ShapeState2D,
} from '../../../../../cross-team-shared/annotation/shared/state/shape-state';
import { getOrThrow } from '../../../../../cross-team-shared/misc-utils/strict-record';
import { AbstractAnnotationStore } from '../../../../../outputs/annotation/annotation-shared/state/root/abstract-annotation-store';
import { PointCloudContentConnector } from '../../../../../team-apis/content/content-connector';
import {
  Point2D,
  ShapeLocation,
} from '../../../../../team-apis/otterdam/answers/shape-typings';
import { outputInfoStoreToken } from '../../../../h3ml/state/output-info';
import { TaskWorkspaceStore } from '../../../../h3ml/state/task-workspace-store';
import { AnnotationCreateDeleteService } from '../../../../shared/misc-services/annotation-create-delete.service';
import { contentConnectorToken } from '../../utils/misc-utils';
import { FusionManager } from '../fusion/fusion-manager';
import { FusionMinion } from '../fusion/fusion-minion';
import { PushAndTrackService } from './push-and-track.service';

@Injectable()
export class VectorToPointCloudShapeService {
  private injector = inject(Injector);
  private fusionManager = inject(FusionManager);
  private taskWorkspaceStore = inject(TaskWorkspaceStore);
  private outputInfoStore = inject(outputInfoStoreToken);
  private store = inject(AbstractAnnotationStore);
  private pushAndTrackService = inject(PushAndTrackService);
  private annotationCreateDeleteService = inject(AnnotationCreateDeleteService);

  upsertProjectedCuboid(originShapeId: number) {
    const outputName = this.outputInfoStore.state().output.name;
    const shapes = this.taskWorkspaceStore.getShapesStore(outputName).state();
    const shape = getOrThrow(shapes, originShapeId) as ShapeState2D;
    const primaryMinion = this.fusionManager.getPrimaryMinion();
    const pointCloudOutputName = primaryMinion.output.name;

    const cuboid = this.buildCuboid(primaryMinion, shape, outputName);

    cuboid.index =
      shape.linkedShapeId ?? this.taskWorkspaceStore.claimNextInternalId();
    cuboid.linkedShapeId = shape.index;
    cuboid.outputName = pointCloudOutputName;

    const shapeStore = this.taskWorkspaceStore.getShapeStore(
      outputName,
      originShapeId,
    );
    shapeStore('linkedShapeId').set(cuboid.index);
    this.upsertCuboid(primaryMinion, pointCloudOutputName, cuboid);

    this.pushAndTrackService.pushAndTrack({
      id: 'create_shape',
      bucket: 'annotation',
      shapes: [getShapeForEventTracking(cuboid)],
      method: '2d_context_menu',
    });
  }

  private buildCuboid(
    primaryMinion: FusionMinion,
    shape: ShapeState2D,
    outputName: string,
  ) {
    const contentConnector =
      primaryMinion.contentConnector as PointCloudContentConnector;

    const location = this.injector
      .get(contentConnectorToken)
      .determineLocation(
        this.store.state().frameNumber,
        shape.index,
      ) as ShapeLocation<Point2D>;

    return contentConnector.buildProjectedCuboid(
      outputName,
      location.points,
      shape,
    );
  }

  private upsertCuboid(
    primaryMinion: FusionMinion,
    pointCloudOutputName: string,
    cuboid: ShapeState,
  ) {
    if (primaryMinion.hasShape(cuboid.index)) {
      this.taskWorkspaceStore
        .getShapesStore(pointCloudOutputName)(cuboid.index)
        .set(cuboid);
    } else {
      this.annotationCreateDeleteService.createShape(
        cuboid,
        pointCloudOutputName,
        false,
        cuboid.parentId,
      );
    }
  }
}
